/**
 * Pure combat presentation math (no Phaser, no feature catalogs).
 * Role name lists are caller-supplied — engine does not hardcode FFA roles.
 */

export type Pt = { x: number; y: number };

/** Resolve muzzle (or keep body center when no rig). */
export function resolveAttackOrigin(
  x0: number,
  y0: number,
  muzzle: { x: number; y: number } | null | undefined,
): { x: number; y: number } {
  if (muzzle && Number.isFinite(muzzle.x) && Number.isFinite(muzzle.y)) {
    return { x: muzzle.x, y: muzzle.y };
  }
  return { x: x0, y: y0 };
}

/** Fan points for a cone telegraph (origin + arc). */
export function coneTelegraphPoints(
  x: number,
  y: number,
  aimAngle: number,
  range: number,
  coneDeg: number,
  segments = 8,
): Pt[] {
  const half = ((Math.max(8, coneDeg) * Math.PI) / 180) * 0.5;
  const segs = Math.max(3, segments);
  const pts: Pt[] = [{ x, y }];
  for (let i = 0; i <= segs; i++) {
    const t = i / segs;
    const a = aimAngle - half + t * half * 2;
    pts.push({
      x: x + Math.cos(a) * range,
      y: y + Math.sin(a) * range,
    });
  }
  return pts;
}

/**
 * Axis-aligned corridor quad for a line telegraph (origin → tip).
 * `progress` 0..1 shortens the filled segment from the origin.
 */
export function lineTelegraphQuad(
  x: number,
  y: number,
  aimAngle: number,
  range: number,
  halfWidth: number,
  progress = 1,
): Pt[] {
  const p = Math.min(1, Math.max(0, progress));
  const len = Math.max(0, range) * p;
  if (len < 1e-3) return [];
  const fx = Math.cos(aimAngle);
  const fy = Math.sin(aimAngle);
  const px = -fy;
  const py = fx;
  const hw = Math.max(1, halfWidth);
  const tipX = x + fx * len;
  const tipY = y + fy * len;
  return [
    { x: x + px * hw, y: y + py * hw },
    { x: tipX + px * hw, y: tipY + py * hw },
    { x: tipX - px * hw, y: tipY - py * hw },
    { x: x - px * hw, y: y - py * hw },
  ];
}

/**
 * AttackRange telegraph fill 0..1 from sim remain/duration.
 * 1 when remain hits 0 (damage resolves for slam/brass).
 */
export function telegraphFillProgress(remain: number, duration: number): number {
  const d = Math.max(1e-6, duration);
  return Math.min(1, Math.max(0, 1 - Math.max(0, remain) / d));
}

/**
 * Leap land telegraph fill 0..1 spanning windup + travel.
 * 1 when land damage resolves (end of travel).
 */
export function leapTelegraphFillProgress(opts: {
  phase: "windup" | "travel";
  remain: number;
  phaseDuration: number;
  windupSec: number;
  travelSec: number;
}): number {
  const windup = Math.max(1e-6, opts.windupSec);
  const travel = Math.max(1e-6, opts.travelSec);
  const total = windup + travel;
  if (opts.phase === "windup") {
    const elapsed = Math.max(0, opts.phaseDuration - Math.max(0, opts.remain));
    return Math.min(1, Math.max(0, elapsed / total));
  }
  const travelElapsed = Math.max(
    0,
    opts.phaseDuration - Math.max(0, opts.remain),
  );
  return Math.min(1, Math.max(0, (windup + travelElapsed) / total));
}

/** Map attack style / hit mode → FFA-style VFX preset id (`ffa.hit.*`). */
export function hitPresetId(style: string, mode: string): string {
  if (mode === "circle") return "ffa.hit.artillery";
  if (mode === "line") return "ffa.hit.thrust";
  switch (style) {
    case "thrust":
      return "ffa.hit.thrust";
    case "bash":
      return "ffa.hit.bash";
    case "poke":
      return "ffa.hit.poke";
    case "recoil":
      return "ffa.hit.artillery";
    case "slash":
    default:
      return "ffa.hit.slash";
  }
}

/** Whether role is in the caller-supplied artillery set. */
export function isArtilleryRole(
  role: string,
  artilleryRoles: readonly string[],
): boolean {
  return artilleryRoles.includes(role);
}

/**
 * Hit particle VFX deferred to Attack travel.
 * True when `ranged` or role is in `deferredRoles`.
 */
export function hitVfxDeferredToAttack(
  role: string,
  opts: { ranged?: boolean; deferredRoles?: readonly string[] } = {},
): boolean {
  if (opts.ranged) return true;
  const list = opts.deferredRoles ?? [];
  return list.includes(role);
}
